import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus,
  HardHat,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Eye,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

const PPE_TYPES = [
  { value: 'hard_hat', label: 'Hard Hat' },
  { value: 'safety_vest', label: 'Safety Vest' },
  { value: 'gloves', label: 'Gloves' },
  { value: 'steel_toe_boots', label: 'Steel Toe Boots' },
  { value: 'safety_glasses', label: 'Safety Glasses' },
  { value: 'harness', label: 'Fall Protection Harness' },
  { value: 'respirator', label: 'Respirator' },
  { value: 'hearing_protection', label: 'Hearing Protection' }
];

const CONDITIONS = [
  { value: 'good', label: 'Good', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'damaged', label: 'Damaged', color: 'bg-amber-100 text-amber-700' },
  { value: 'missing', label: 'Missing', color: 'bg-rose-100 text-rose-700' }
];

export default function AdminPPEInspections() {
  const { authAxios, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [inspections, setInspections] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);

  const defaultItems = PPE_TYPES.map(p => ({
    ppe_type: p.value,
    condition: 'good',
    action_required: ''
  }));

  const [formData, setFormData] = useState({
    employee_id: isAdmin ? '' : user?.id,
    project_id: '',
    items: [...defaultItems],
    overall_compliance: 'compliant',
    notes: '',
    signature: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [inspRes, empRes, projRes] = await Promise.all([
        authAxios.get('/forms/ppe-inspections'),
        isAdmin ? authAxios.get('/employees') : Promise.resolve({ data: [user] }),
        authAxios.get('/projects')
      ]);
      setInspections(inspRes.data);
      setEmployees(empRes.data);
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [authAxios, isAdmin, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      employee_id: isAdmin ? '' : user?.id,
      project_id: '',
      items: [...defaultItems],
      overall_compliance: 'compliant',
      notes: '',
      signature: ''
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Auto-calculate compliance
    const hasIssues = newItems.some(item => item.condition !== 'good');
    setFormData({
      ...formData,
      items: newItems,
      overall_compliance: hasIssues ? 'non_compliant' : 'compliant'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.signature) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await authAxios.post('/forms/ppe-inspections', {
        ...formData,
        project_id: formData.project_id && formData.project_id !== 'none' ? formData.project_id : null
      });
      toast.success('PPE inspection submitted');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inspection');
    } finally {
      setSubmitting(false);
    }
  };

  const exportPDF = async (inspectionId) => {
    try {
      const response = await authAxios.get(`/pdf/ppe-inspection/${inspectionId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ppe_inspection_${inspectionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const getComplianceBadge = (compliance) => {
    if (compliance === 'compliant') {
      return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Compliant</Badge>;
    }
    return <Badge className="bg-rose-100 text-rose-700"><XCircle className="w-3 h-3 mr-1" />Non-Compliant</Badge>;
  };

  const getConditionBadge = (condition) => {
    const cond = CONDITIONS.find(c => c.value === condition);
    return <Badge className={cond?.color || 'bg-stone-100 text-stone-700'}>{cond?.label || condition}</Badge>;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="ppe-inspections">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">PPE Inspections</h1>
          <p className="text-stone-500 mt-1">Personal Protective Equipment compliance checks</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="new-ppe-inspection-btn">
              <Plus className="w-5 h-5 mr-2" />
              New Inspection
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HardHat className="w-5 h-5 text-emerald-600" />
                PPE Inspection Form
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee *</Label>
                  <Select value={formData.employee_id} onValueChange={(v) => setFormData({ ...formData, employee_id: v })}>
                    <SelectTrigger data-testid="ppe-employee-select" disabled={!isAdmin}>
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Project (Optional)</Label>
                  <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <Label>PPE Items</Label>
                <div className="border rounded-lg divide-y">
                  {formData.items.map((item, idx) => {
                    const ppeType = PPE_TYPES.find(p => p.value === item.ppe_type);
                    return (
                      <div key={idx} className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{ppeType?.label}</span>
                          <Select 
                            value={item.condition} 
                            onValueChange={(v) => updateItem(idx, 'condition', v)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CONDITIONS.map(c => (
                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {item.condition !== 'good' && (
                          <Input
                            placeholder="Action required (e.g., Replace, Repair)"
                            value={item.action_required}
                            onChange={(e) => updateItem(idx, 'action_required', e.target.value)}
                            className="text-sm"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg">
                <span className="font-medium">Overall Compliance:</span>
                {getComplianceBadge(formData.overall_compliance)}
                {formData.overall_compliance === 'non_compliant' && (
                  <span className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Action required
                  </span>
                )}
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional observations or comments..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Inspector Signature (Type Name) *</Label>
                <Input
                  value={formData.signature}
                  onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                  placeholder="Type your full name as signature"
                  required
                  data-testid="ppe-signature"
                />
              </div>

              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Submit Inspection
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Inspections Table */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <HardHat className="w-5 h-5 text-stone-400" />
            PPE Inspections ({inspections.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : inspections.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <HardHat className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No PPE inspections found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Inspector</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Compliance</TableHead>
                    <TableHead>Issues</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right sticky right-0 bg-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inspections.map((insp) => {
                    const issues = insp.items?.filter(i => i.condition !== 'good') || [];
                    return (
                      <TableRow key={insp.id}>
                        <TableCell className="font-medium">{insp.employee_name}</TableCell>
                        <TableCell>{insp.inspector_name}</TableCell>
                        <TableCell>{insp.project_name || '-'}</TableCell>
                        <TableCell>{getComplianceBadge(insp.overall_compliance)}</TableCell>
                        <TableCell>
                          {issues.length > 0 ? (
                            <span className="text-amber-600 text-sm">{issues.length} item(s)</span>
                          ) : (
                            <span className="text-emerald-600 text-sm">None</span>
                          )}
                        </TableCell>
                        <TableCell>{fmtTz(insp.created_at, 'MMM d, yyyy h:mm a')}</TableCell>
                        <TableCell className="text-right space-x-1 sticky right-0 bg-white whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedInspection(insp); setViewDialogOpen(true); }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => exportPDF(insp.id)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={async () => {
                                if (!window.confirm('Delete this PPE inspection permanently?')) return;
                                try {
                                  await authAxios.delete(`/forms/ppe-inspections/${insp.id}`);
                                  toast.success('PPE inspection deleted');
                                  fetchData();
                                } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
                              }}
                              data-testid={`delete-ppe-${insp.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-rose-600" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Inspection Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>PPE Inspection Details</DialogTitle>
          </DialogHeader>
          {selectedInspection && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-stone-500">Employee</p>
                  <p className="font-medium">{selectedInspection.employee_name}</p>
                </div>
                <div>
                  <p className="text-stone-500">Inspector</p>
                  <p className="font-medium">{selectedInspection.inspector_name}</p>
                </div>
                <div>
                  <p className="text-stone-500">Project</p>
                  <p className="font-medium">{selectedInspection.project_name || '-'}</p>
                </div>
                <div>
                  <p className="text-stone-500">Compliance</p>
                  {getComplianceBadge(selectedInspection.overall_compliance)}
                </div>
              </div>
              <div>
                <p className="text-stone-500 text-sm mb-2">PPE Items</p>
                <div className="border rounded-lg divide-y">
                  {selectedInspection.items?.map((item, idx) => {
                    const ppeType = PPE_TYPES.find(p => p.value === item.ppe_type);
                    return (
                      <div key={idx} className="flex items-center justify-between p-2 text-sm">
                        <span>{ppeType?.label}</span>
                        <div className="text-right">
                          {getConditionBadge(item.condition)}
                          {item.action_required && (
                            <p className="text-xs text-amber-600 mt-1">{item.action_required}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {selectedInspection.notes && (
                <div>
                  <p className="text-stone-500 text-sm">Notes</p>
                  <p className="text-sm">{selectedInspection.notes}</p>
                </div>
              )}
              <div className="pt-4 border-t text-sm">
                <p className="text-stone-500">Inspector Signature</p>
                <p className="font-medium italic">{selectedInspection.signature}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
